'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

const IS_DEV = process.env.NODE_ENV !== 'production';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    console.error('[global-error boundary]', error.digest ?? error.message, error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily: 'sans-serif',
          background: '#fff',
          color: '#171717',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          textAlign: 'center',
          padding: '0 24px',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            border: '2px solid #BE1E2D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <span style={{ fontSize: 28, color: '#BE1E2D', lineHeight: 1, fontWeight: 700 }}>!</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Something went wrong</h1>
        <p style={{ color: '#666', maxWidth: 480, lineHeight: 1.7, marginBottom: 32 }}>
          We encountered an unexpected error. Please try again — if the issue persists, contact support.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={reset}
            style={{
              padding: '10px 28px',
              background: 'var(--color-primary, #006300)',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 15,
            }}
          >
            Try again
          </button>
          <Link
            href="/"
            style={{
              padding: '10px 28px',
              border: '1px solid #ccc',
              fontWeight: 700,
              fontSize: 15,
              textDecoration: 'none',
              color: '#333',
            }}
          >
            Go home
          </Link>
        </div>

        {IS_DEV && (
          <div style={{ marginTop: 32, width: '100%', maxWidth: 640, textAlign: 'left' }}>
            <button
              type="button"
              onClick={() => setDetailsOpen(o => !o)}
              aria-expanded={detailsOpen}
              style={{
                background: 'none',
                border: 'none',
                color: '#444',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}
            >
              {detailsOpen ? '▲ Hide error details' : '▼ Show error details (dev only)'}
            </button>
            {detailsOpen && (
              <div
                style={{
                  marginTop: 12,
                  border: '1px solid #E9EAEB',
                  background: '#FAFAFA',
                  padding: 16,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 320,
                  lineHeight: 1.6,
                }}
              >
                <div style={{ marginBottom: 8 }}>
                  <strong>message:</strong> {error.message || '(no message)'}
                </div>
                {error.digest && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>digest:</strong> {error.digest}
                  </div>
                )}
                {error.stack && (
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, opacity: 0.8 }}>
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </body>
    </html>
  );
}
